/**
 * Рисунки для развёртки: спорт, детское и прочая мебель раздела «Мебель» — тренажёры,
 * велосипед, пианино, двухъярусные кровати, барные стулья, остров, вешалки, ширма.
 *
 * Те же правила, что у остальной мебели (`elevationSymbols`): контур в миллиметрах типового
 * предмета, низ — пол, сбоку перед смотрит вправо. У длинных тренажёров и велосипеда
 * «спереди» по каталогу — их длинная сторона: ширина у них идёт вдоль рамы, поэтому
 * спереди рисуем профиль, а сбоку — узкий вид с торца.
 */
import { UIKit, type ElevationSymbols, type SymbolComponent } from "@/shared/ui";
import { Fronts, Legs, handle, line, thin } from "../../elevationParts";

/* ——— Спорт ——— */

/** Велотренажёр в профиль: маховик спереди, седло, руль, опоры */
const ExerciseBikeFront: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 1200 1260" {...props}>
		<path d="M100 1240H560M640 1240H1100" {...line} strokeLinecap="round" />
		<path d="M330 1240L470 700M900 1240L880 420M470 980H860" {...line} />
		<circle cx="860" cy="980" r="190" {...line} />
		<circle cx="560" cy="980" r="60" {...thin} />
		<rect x="360" y="650" width="220" height="50" rx="20" {...line} />
		<path d="M820 400H1000" {...line} strokeLinecap="round" />
	</UIKit.SymbolFrame>
);

/** Велотренажёр с торца: узкая стойка, руль поперёк, опора */
const ExerciseBikeSide: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 520 1260" {...props}>
		<path d="M20 1240H500" {...line} strokeLinecap="round" />
		<rect x="200" y="760" width="120" height="440" rx="20" {...line} />
		<rect x="230" y="420" width="60" height="340" {...thin} />
		<path d="M60 410H460" {...line} strokeLinecap="round" />
	</UIKit.SymbolFrame>
);

/** Беговая дорожка с торца: стойки с пультом, полотно внизу */
const TreadmillFront: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 970 1450" {...props}>
		<path d="M110 1260L150 350M860 1260L820 350" {...line} />
		<rect x="120" y="230" width="730" height="200" rx="30" {...line} />
		<rect x="300" y="270" width="370" height="110" rx="10" {...thin} />
		<rect x="30" y="1260" width="910" height="160" rx="30" {...line} />
	</UIKit.SymbolFrame>
);

/** Беговая дорожка сбоку: длинное полотно, стойка с пультом спереди (справа) */
const TreadmillSide: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 1340 1450" {...props}>
		<rect x="20" y="1260" width="1300" height="160" rx="60" {...line} />
		<path d="M1150 1260L1080 350" {...line} />
		<path d="M1000 320L1200 300L1220 420L1030 440Z" {...line} strokeLinejoin="round" />
		<path d="M1060 560H880" {...line} strokeLinecap="round" />
	</UIKit.SymbolFrame>
);

/** Гребной тренажёр с торца: монитор на стойке, рельс */
const RowingFront: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 830 1700" {...props}>
		<rect x="300" y="300" width="230" height="160" rx="16" {...line} />
		<path d="M415 460V1500" {...line} />
		<circle cx="415" cy="1400" r="200" {...line} />
		<path d="M60 1680H770" {...line} strokeLinecap="round" />
	</UIKit.SymbolFrame>
);

/** Гребной тренажёр сбоку: длинный рельс, сиденье, маховик и монитор спереди */
const RowingSide: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 1800 1700" {...props}>
		<path d="M40 1600L1450 1500" {...line} strokeLinecap="round" />
		<path d="M40 1600V1690M1450 1500V1690" {...thin} />
		<circle cx="1520" cy="1400" r="200" {...line} />
		<rect x="500" y="1460" width="260" height="60" rx="20" {...line} />
		<path d="M1500 1220L1600 460" {...line} />
		<rect x="1500" y="300" width="230" height="160" rx="16" {...line} />
	</UIKit.SymbolFrame>
);

/** Скамья для жима с торца: подушка на опоре */
const WeightBenchFront: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 520 940" {...props}>
		<rect x="40" y="380" width="440" height="80" rx="30" {...line} />
		<rect x="230" y="460" width="60" height="400" {...thin} />
		<path d="M30 920H490" {...line} strokeLinecap="round" />
	</UIKit.SymbolFrame>
);

/** Скамья для жима сбоку: сиденье и поднятая спинка */
const WeightBenchSide: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 1300 940" {...props}>
		<path d="M200 920L560 470M600 470L760 920M1150 920L1080 480" {...line} />
		<rect x="560" y="400" width="600" height="80" rx="30" {...line} />
		<path d="M140 120L560 400L520 470L90 190Z" {...line} strokeLinejoin="round" />
	</UIKit.SymbolFrame>
);

/** Стойка с гантелями: два яруса, на каждом гантели блинами к нам */
const DumbbellRackFront: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 800 550" {...props}>
		<path d="M60 540L120 60M740 540L680 60M100 220H700M80 420H720" {...line} />
		{[180, 320, 480, 620].map((x) => (
			<g key={x}>
				<circle cx={x} cy="170" r="45" {...thin} />
				<circle cx={x} cy="365" r="55" {...thin} />
			</g>
		))}
	</UIKit.SymbolFrame>
);

/** Боксёрский мешок на стойке: рама, цепь и мешок */
const PunchingBagFront: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 1430 2200" {...props}>
		<path d="M200 2190V60H1100M100 2190H500" {...line} />
		<path d="M1000 60V300" {...thin} />
		<rect x="860" y="300" width="280" height="1100" rx="80" {...line} />
		<path d="M860 500H1140M860 1200H1140" {...thin} />
	</UIKit.SymbolFrame>
);

/** Шведская стенка: две стойки и перекладины */
const WallBarsFront: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 1100 2400" {...props}>
		<rect x="20" y="10" width="70" height="2390" rx="10" {...line} />
		<rect x="1010" y="10" width="70" height="2390" rx="10" {...line} />
		{Array.from({ length: 15 }, (_, i) => 150 + i * 150).map((y) => (
			<path key={y} d={`M90 ${y}H1010`} {...line} />
		))}
	</UIKit.SymbolFrame>
);

/** Велосипед в профиль: два колеса, рама, седло и руль */
const BicycleFront: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 1750 960" {...props}>
		<circle cx="340" cy="620" r="330" {...line} />
		<circle cx="1410" cy="620" r="330" {...line} />
		<path
			d="M340 620L760 620L620 300L1180 300L1410 620M760 620L1180 300M620 300L580 210M1180 300L1220 150"
			{...line}
			strokeLinejoin="round"
		/>
		<path d="M480 200H660" {...line} strokeLinecap="round" />
		<path d="M1180 150H1300" {...line} strokeLinecap="round" />
	</UIKit.SymbolFrame>
);

/** Велосипед с торца: колесо ребром, руль поперёк */
const BicycleSide: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 490 960" {...props}>
		<rect x="225" y="290" width="40" height="660" rx="20" {...line} />
		<path d="M30 150H460" {...line} strokeLinecap="round" />
		<path d="M245 150V290" {...thin} />
	</UIKit.SymbolFrame>
);

/* ——— Музыка ——— */

/** Пианино: корпус, клавиатура на полке, педали и ножки */
const PianoFront: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 1500 1380" {...props}>
		<rect x="20" y="10" width="1460" height="1370" rx="10" {...line} />
		<rect x="60" y="80" width="1380" height="560" {...thin} />
		<rect x="40" y="640" width="1420" height="120" {...line} />
		<path d="M80 700H1420" {...thin} />
		{Array.from({ length: 22 }, (_, i) => 120 + i * 60).map((x) => (
			<path key={x} d={`M${x} 640V700`} stroke="currentColor" strokeWidth="5" />
		))}
		<path d="M680 1340H720M750 1340H790M820 1340H860" {...handle} />
	</UIKit.SymbolFrame>
);

/* ——— Детское ——— */

/** Кровать-чердак: спальное место наверху с бортиком, лестница, под ним свободно */
const LoftBedFront: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 2050 1800" {...props}>
		<rect x="10" y="10" width="80" height="1790" {...line} />
		<rect x="1960" y="10" width="80" height="1790" {...line} />
		<rect x="90" y="1060" width="1870" height="150" {...line} />
		<rect x="110" y="960" width="1830" height="100" rx="30" {...thin} />
		<path d="M90 600H1500M90 780H1500" {...line} />
		<path d="M1550 600V1800M1800 600V1800M1550 1350H1800M1550 1580H1800" {...thin} />
	</UIKit.SymbolFrame>
);

/** Кровать-чердак с торца: две стойки и один ярус наверху с бортиком */
const LoftBedSide: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 1130 1800" {...props}>
		<rect x="10" y="10" width="80" height="1790" {...line} />
		<rect x="1040" y="10" width="80" height="1790" {...line} />
		<rect x="90" y="960" width="950" height="250" {...line} />
		<path d="M90 600H1040M90 780H1040" {...line} />
	</UIKit.SymbolFrame>
);

/** Кровать-домик: каркас с двускатной крышей, матрас внизу */
const HouseBedFront: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 1700 2050" {...props}>
		<path d="M60 2040V900L850 60L1640 900V2040" {...line} strokeLinejoin="round" />
		<rect x="40" y="1700" width="1620" height="240" {...line} />
		<rect x="70" y="1600" width="1560" height="100" rx="30" {...thin} />
		<Legs left={60} right={1640} top={1940} floor={2050} width={60} />
	</UIKit.SymbolFrame>
);

/** Детская кроватка: решётчатые стенки на ножках */
const CribFront: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 1250 875" {...props}>
		<rect x="10" y="10" width="60" height="865" {...line} />
		<rect x="1180" y="10" width="60" height="865" {...line} />
		<path d="M70 120H1180M70 640H1180" {...line} />
		{Array.from({ length: 15 }, (_, i) => 140 + i * 70).map((x) => (
			<path key={x} d={`M${x} 120V640`} {...thin} />
		))}
	</UIKit.SymbolFrame>
);

/** Пеленальный комод: три ящика, сверху пеленальный матрасик с бортиками */
const ChangingTableFront: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 900 810" {...props}>
		<path d="M20 10V140H880V10" {...line} />
		<rect x="40" y="90" width="820" height="50" rx="20" {...thin} />
		<rect x="10" y="140" width="880" height="600" {...line} />
		<Fronts x={10} y={140} width={880} height={600} count={1} rows={3} drawers />
		<Legs left={30} right={870} top={740} floor={810} width={50} />
	</UIKit.SymbolFrame>
);

/** Детский стульчик для кормления: высокие ножки, сиденье со столиком */
const HighChairFront: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 600 900" {...props}>
		<path d="M120 890L200 450M480 890L400 450" {...line} />
		<rect x="180" y="100" width="240" height="320" rx="40" {...line} />
		<rect x="140" y="400" width="320" height="60" rx="20" {...line} />
		<path d="M200 640H400" {...thin} />
	</UIKit.SymbolFrame>
);

/** Детский столик со стульчиками */
const KidsTableSetFront: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 1000 580" {...props}>
		<rect x="40" y="80" width="140" height="200" rx="20" {...thin} />
		<rect x="20" y="280" width="190" height="40" {...line} />
		<path d="M40 320V575M190 320V575" {...thin} />
		<rect x="820" y="80" width="140" height="200" rx="20" {...thin} />
		<rect x="790" y="280" width="190" height="40" {...line} />
		<path d="M810 320V575M960 320V575" {...thin} />
		<rect x="250" y="180" width="500" height="40" {...line} />
		<Legs left={280} right={720} top={220} floor={580} width={40} />
	</UIKit.SymbolFrame>
);

/** Лошадка-качалка в профиль: голова, седло, дуги-полозья */
const RockingHorseSide: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 900 840" {...props}>
		<path d="M40 700Q450 900 860 700" {...line} />
		<path d="M220 760L260 520M680 760L640 520" {...line} />
		<path d="M200 400Q450 360 700 400L700 520H200Z" {...line} />
		<path
			d="M650 400L700 120Q760 60 840 130L800 220L740 220L720 400"
			{...line}
			strokeLinejoin="round"
		/>
		<rect x="350" y="330" width="200" height="60" rx="20" {...thin} />
	</UIKit.SymbolFrame>
);

/** Лошадка-качалка спереди: голова над узким корпусом и полозья */
const RockingHorseFront: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 370 840" {...props}>
		<path d="M20 790H350" {...line} strokeLinecap="round" />
		<path d="M80 790L120 520M290 790L250 520" {...line} />
		<rect x="110" y="360" width="150" height="180" rx="40" {...line} />
		<rect x="135" y="80" width="100" height="280" rx="40" {...line} />
	</UIKit.SymbolFrame>
);

/** Вигвам: шесты, сходящиеся вверху, и вход */
const TeepeeFront: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 1300 1480" {...props}>
		<path d="M30 1470L650 200L1270 1470Z" {...line} strokeLinejoin="round" />
		<path d="M650 200L540 10M650 200L760 10" {...line} strokeLinecap="round" />
		<path d="M650 700L420 1470M650 700L880 1470" {...thin} />
	</UIKit.SymbolFrame>
);

/* ——— Прочая мебель ——— */

/** Туалетный столик: круглое зеркало, столешница с ящиком, ножки */
const DressingTableFront: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 1000 1500" {...props}>
		<circle cx="500" cy="420" r="320" {...line} />
		<circle cx="500" cy="420" r="280" {...thin} />
		<rect x="10" y="740" width="980" height="160" {...line} />
		<path d="M420 820H580" {...handle} />
		<Legs left={40} right={960} top={900} floor={1500} width={50} />
	</UIKit.SymbolFrame>
);

/** Туалетный столик сбоку: зеркало ребром у стены, столешница и ножки */
const DressingTableSide: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 650 1500" {...props}>
		<rect x="40" y="100" width="40" height="640" rx="14" {...line} />
		<rect x="10" y="740" width="630" height="160" {...line} />
		<Legs left={30} right={620} top={900} floor={1500} width={50} />
	</UIKit.SymbolFrame>
);

/** Напольная вешалка-рейл: штанга на двух стойках, несколько плечиков */
const ClothesRackFront: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 1250 1600" {...props}>
		<path d="M80 1590V60H1170V1590M20 1590H240M1010 1590H1230" {...line} />
		{[300, 450, 600, 750, 900].map((x) => (
			<path key={x} d={`M${x} 60V110L${x - 90} 190H${x + 90}Z`} {...thin} />
		))}
	</UIKit.SymbolFrame>
);

/** Вешалка-стойка: штанга с крючками и основание */
const CoatStandFront: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 600 1750" {...props}>
		<path d="M300 40V1700" {...line} />
		<path
			d="M300 140L160 60M300 140L440 60M300 260L180 200M300 260L420 200"
			{...thin}
			strokeLinecap="round"
		/>
		<path d="M300 1600L80 1740M300 1600L520 1740" {...line} strokeLinecap="round" />
	</UIKit.SymbolFrame>
);

/** Ширма: четыре створки */
const RoomDividerFront: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 1600 1540" {...props}>
		{[0, 1, 2, 3].map((i) => (
			<g key={i}>
				<rect x={10 + i * 395} y="10" width="385" height="1470" rx="10" {...line} />
				<rect x={50 + i * 395} y="60" width="305" height="1370" {...thin} />
			</g>
		))}
		<Legs left={40} right={1560} top={1480} floor={1540} width={60} />
	</UIKit.SymbolFrame>
);

/** Барный стул: сиденье, стойка, подножка и основание */
const BarStoolFront: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 490 750" {...props}>
		<rect x="40" y="10" width="410" height="70" rx="30" {...line} />
		<rect x="225" y="80" width="40" height="620" {...thin} />
		<path d="M130 480H360" {...line} strokeLinecap="round" />
		<rect x="110" y="700" width="270" height="45" rx="20" {...line} />
	</UIKit.SymbolFrame>
);

/** Барный набор: высокий стол и два стула по бокам */
const BarSetFront: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 1400 1060" {...props}>
		<rect x="40" y="350" width="280" height="60" rx="25" {...line} />
		<path d="M180 410V1000M100 760H260M100 1050L180 1000L260 1050" {...line} />
		<rect x="1080" y="350" width="280" height="60" rx="25" {...line} />
		<path d="M1220 410V1000M1140 760H1300M1140 1050L1220 1000L1300 1050" {...line} />
		<rect x="380" y="10" width="640" height="60" {...line} />
		<rect x="680" y="70" width="40" height="940" {...thin} />
		<rect x="520" y="1010" width="360" height="45" rx="15" {...line} />
	</UIKit.SymbolFrame>
);

/** Кухонный остров: столешница со свесом, шкафы под ней */
const KitchenIslandFront: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 1800 900" {...props}>
		<rect x="10" y="10" width="1780" height="50" {...line} />
		<rect x="60" y="60" width="1680" height="740" {...line} />
		<Fronts x={60} y={60} width={1680} height={740} count={3} />
		<rect x="90" y="800" width="1620" height="100" {...thin} />
	</UIKit.SymbolFrame>
);

/** Кухонный остров с торца: свес столешницы над местом для ног */
const KitchenIslandSide: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 1000 900" {...props}>
		<rect x="10" y="10" width="980" height="50" {...line} />
		<rect x="300" y="60" width="650" height="740" {...line} />
		<rect x="330" y="800" width="590" height="100" {...thin} />
	</UIKit.SymbolFrame>
);

/** Стол со столиком-стойкой: геймерский набор — стол и два монитора на нём */
const GamingSetupFront: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 1600 1280" {...props}>
		<rect x="280" y="200" width="500" height="300" rx="10" {...line} />
		<rect x="820" y="200" width="500" height="300" rx="10" {...line} />
		<path d="M530 500V530M1070 500V530" {...thin} />
		<rect x="10" y="530" width="1580" height="45" {...line} />
		<rect x="40" y="575" width="60" height="705" {...line} />
		<rect x="1500" y="575" width="60" height="705" {...line} />
		<rect x="1180" y="790" width="230" height="480" rx="10" {...thin} />
	</UIKit.SymbolFrame>
);

/** Геймерский набор сбоку: монитор ребром на столе, стол на ножках */
const GamingSetupSide: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 815 1280" {...props}>
		<rect x="120" y="200" width="40" height="300" rx="10" {...line} />
		<path d="M140 500V530" {...thin} />
		<rect x="10" y="530" width="795" height="45" {...line} />
		<rect x="40" y="575" width="60" height="705" {...line} />
		<rect x="715" y="575" width="60" height="705" {...line} />
	</UIKit.SymbolFrame>
);

/** Угловой стол: столешница и надстройка-полка над ней */
const CornerDeskFront: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 1400 1000" {...props}>
		<rect x="40" y="10" width="1320" height="40" {...line} />
		<path d="M60 50V250M1340 50V250" {...line} />
		<rect x="10" y="250" width="1380" height="40" {...line} />
		<rect x="40" y="290" width="50" height="710" {...line} />
		<rect x="1310" y="290" width="50" height="710" {...line} />
	</UIKit.SymbolFrame>
);

/** Журнальные столики-матрёшки: высокий и низкий рядом */
const NestingTablesFront: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 1000 450" {...props}>
		<rect x="380" y="140" width="610" height="35" {...line} />
		<Legs left={400} right={970} top={175} floor={450} width={35} />
		<rect x="10" y="10" width="560" height="35" {...line} />
		<Legs left={30} right={550} top={45} floor={450} width={35} />
	</UIKit.SymbolFrame>
);

/** Скамья со спинкой: сиденье, спинка из реек, ножки */
const BenchBackFront: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 1400 920" {...props}>
		<rect x="40" y="20" width="1320" height="380" rx="20" {...line} />
		<path d="M40 140H1360M40 260H1360" {...thin} />
		<rect x="10" y="430" width="1380" height="70" rx="15" {...line} />
		<Legs left={40} right={1360} top={500} floor={920} width={60} />
	</UIKit.SymbolFrame>
);

/** Скамья со спинкой сбоку: сиденье вперёд, спинка сзади с наклоном */
const BenchBackSide: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 490 920" {...props}>
		<path d="M40 20H110L130 430H60Z" {...line} />
		<rect x="40" y="430" width="440" height="70" rx="15" {...line} />
		<Legs left={60} right={460} top={500} floor={920} width={50} />
	</UIKit.SymbolFrame>
);

/** Сушилка для белья: верхние струны и складные опоры */
const DryingRackFront: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 1300 1030" {...props}>
		<rect x="200" y="300" width="900" height="40" rx="10" {...line} />
		<path d="M200 320L20 200M1100 320L1280 200" {...line} strokeLinecap="round" />
		<path d="M300 340L150 1020M1000 340L1150 1020M300 340L520 1020M1000 340L780 1020" {...line} />
	</UIKit.SymbolFrame>
);

/** Бистро-набор: круглый столик на ножке и два стула */
const BistroSetFront: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 1200 600" {...props}>
		<rect x="30" y="10" width="200" height="250" rx="30" {...thin} />
		<rect x="10" y="260" width="240" height="40" {...line} />
		<path d="M30 300V595M230 300V595" {...thin} />
		<rect x="970" y="10" width="200" height="250" rx="30" {...thin} />
		<rect x="950" y="260" width="240" height="40" {...line} />
		<path d="M970 300V595M1170 300V595" {...thin} />
		<rect x="340" y="200" width="520" height="35" {...line} />
		<rect x="580" y="235" width="40" height="330" {...thin} />
		<rect x="480" y="565" width="240" height="30" rx="10" {...line} />
	</UIKit.SymbolFrame>
);

export const FURNITURE_LEISURE_ELEVATIONS: Record<string, ElevationSymbols> = {
	"exercise-bike": { front: ExerciseBikeFront, side: ExerciseBikeSide },
	treadmill: { front: TreadmillFront, side: TreadmillSide },
	"rowing-machine": { front: RowingFront, side: RowingSide },
	"weight-bench": { front: WeightBenchFront, side: WeightBenchSide },
	"dumbbell-rack": { front: DumbbellRackFront },
	"punching-bag": { front: PunchingBagFront, side: PunchingBagFront },
	"wall-bars": { front: WallBarsFront },
	bicycle: { front: BicycleFront, side: BicycleSide },
	piano: { front: PianoFront },
	"bed-loft": { front: LoftBedFront, side: LoftBedSide },
	"bed-house": { front: HouseBedFront, side: HouseBedFront },
	"bed-crib": { front: CribFront, side: CribFront },
	"changing-table": { front: ChangingTableFront },
	"high-chair": { front: HighChairFront, side: HighChairFront },
	"kids-table-set": { front: KidsTableSetFront, side: KidsTableSetFront },
	"rocking-horse": { front: RockingHorseFront, side: RockingHorseSide },
	teepee: { front: TeepeeFront, side: TeepeeFront },
	"dressing-table": { front: DressingTableFront, side: DressingTableSide },
	"clothes-rack": { front: ClothesRackFront, side: ClothesRackFront },
	"coat-stand": { front: CoatStandFront, side: CoatStandFront },
	"room-divider": { front: RoomDividerFront },
	"bar-stool": { front: BarStoolFront, side: BarStoolFront },
	"bar-set": { front: BarSetFront, side: BarSetFront },
	"kitchen-island": { front: KitchenIslandFront, side: KitchenIslandSide },
	"gaming-setup": { front: GamingSetupFront, side: GamingSetupSide },
	"desk-corner": { front: CornerDeskFront, side: CornerDeskFront },
	"coffee-table-nesting": { front: NestingTablesFront, side: NestingTablesFront },
	"bench-back": { front: BenchBackFront, side: BenchBackSide },
	"drying-rack": { front: DryingRackFront, side: DryingRackFront },
	"bistro-set": { front: BistroSetFront, side: BistroSetFront },
	"piano-antique": { front: PianoFront },
	"high-chair-wood": { front: HighChairFront, side: HighChairFront },
	"dressing-table-wood": { front: DressingTableFront, side: DressingTableSide },
	"bar-stool-wood": { front: BarStoolFront, side: BarStoolFront },
};
